import { useState, useMemo } from "react";

// ─── Shared active-recall QuizMode ─────────────────────────────────
//
// Extracted from Anatomy.jsx (V34) — that quiz was an orphan; this is
// the same pattern made reusable so any genuine REFERENCE tool can
// offer recall practice over data it already ships.
//
// Scope decision (locked with Ian): this is CURATED recall over data
// the tool already contains — NOT AI generation. It does not and
// cannot do what the planned paid QuizCard does (turn the user's OWN
// material into cards). Different input, different value, no
// cannibalization. Apply ONLY to reference/lookup tools, never to
// calculators (a calculator has nothing to recall — the answer is
// computed, not memorized).
//
// `cards`: array of { q, a, ctx? }  (prompt, answer, optional context)
// `accent`: the consuming suite's accent color
// `onExit`: called when the user leaves the quiz
//
// NOTE: no QuizCard cross-promo nudge here yet — deliberately added
// only in the pass where the paid QuizCard actually launches (not
// pointing users at an unbuilt route; same honesty rule as not
// shipping a "5/day" number before the model is real).

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function QuizMode({ cards, accent, onExit }) {
  const deck = useMemo(() => shuffle(cards), [cards]);
  const [i, setI] = useState(0);
  const [shown, setShown] = useState(false);
  const [score, setScore] = useState({ right: 0, wrong: 0 });

  if (!deck.length) {
    return (
      <div style={{ textAlign: "center", padding: "28px 12px", color: "#9A9DBE", fontSize: 14 }}>
        Nothing to quiz here yet.
        <div style={{ marginTop: 16 }}>
          <button onClick={onExit} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: accent, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Back</button>
        </div>
      </div>
    );
  }

  if (i >= deck.length) {
    const total = score.right + score.wrong;
    const pct = total ? Math.round((100 * score.right) / total) : 0;
    return (
      <div style={{ textAlign: "center", padding: "32px 12px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: accent }}>{pct}%</div>
        <p style={{ fontSize: 14, color: "#C8CADF", margin: "8px 0 20px" }}>
          {score.right} right · {score.wrong} to review · {deck.length} cards
        </p>
        <button onClick={onExit} style={{ padding: "11px 20px", borderRadius: 10, border: "none", background: accent, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          Back
        </button>
      </div>
    );
  }

  const card = deck[i];
  const next = (correct) => {
    setScore((s) => ({ right: s.right + (correct ? 1 : 0), wrong: s.wrong + (correct ? 0 : 1) }));
    setShown(false);
    setI(i + 1);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#7C8099", marginBottom: 12 }}>
        <span>Card {i + 1} / {deck.length}</span>
        <button onClick={onExit} style={{ background: "none", border: "none", color: "#7C8099", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>exit</button>
      </div>
      <div style={{ background: "#13141F", border: `1px solid ${accent}55`, borderRadius: 14, padding: "26px 20px", minHeight: 150, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {card.ctx && <div style={{ fontSize: 11.5, color: "#7C8099", marginBottom: 6 }}>{card.ctx}</div>}
        <div style={{ fontSize: 19, fontWeight: 700, color: "#E8E9F3" }}>{card.q}</div>
        {shown && <div style={{ fontSize: 13.5, color: "#C8CADF", marginTop: 14, lineHeight: 1.55, borderTop: "1px solid #1C1E2E", paddingTop: 14 }}>{card.a}</div>}
      </div>
      {!shown ? (
        <button onClick={() => setShown(true)} style={{ width: "100%", marginTop: 14, padding: 13, borderRadius: 10, border: `1px solid ${accent}`, background: "transparent", color: accent, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
          Reveal answer
        </button>
      ) : (
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button onClick={() => next(false)} style={{ flex: 1, padding: 13, borderRadius: 10, border: "1px solid #1C1E2E", background: "transparent", color: "#9A9DBE", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
            Review again
          </button>
          <button onClick={() => next(true)} style={{ flex: 1, padding: 13, borderRadius: 10, border: "none", background: accent, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            Got it
          </button>
        </div>
      )}
    </div>
  );
}
